import { Autocomplete, Box, Card, Checkbox, FormControlLabel, LinearProgress, TextField } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '../../../shared/forms';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { VTextField, VTextFieldCurrency, VTextFieldPercentage } from '../../../shared/forms';
import getValidationErrors from '../../../shared/helpers/getValidationErrors';
import { LayoutRegister } from '../../../shared/layouts';
import { AlertTypes, feedback, ICreateServicePackage, IUpdateServicePackage, servicePackageService, serviceService } from '../../../shared/services';

const schema = Yup.object().shape({
  name: Yup.string().required(),
  amount: Yup.number().required('Valor é obrigatório').min(0),
  totalUses: Yup.number().required('Nº de usos é obrigatório').min(1),
  commissionPercent: Yup.number().nullable().min(0).max(100),
});

interface IProps {
  businessId: number;
  packageId?: number;
  refetch: () => void;
  onCloseEdit: () => void;
}

export const BusinessPackagesRegister: React.FC<IProps> = ({ businessId, packageId, refetch, onCloseEdit }) => {
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [active, setActive] = useState(true);

  const { data: pkg, isLoading: isLoadingPackage } = useQuery({
    queryKey: ['servicePackage', packageId],
    queryFn: () => servicePackageService.getById(packageId!),
    enabled: !!packageId,
  });

  const { data: services } = useQuery({
    queryKey: ['services', businessId],
    queryFn: () => serviceService.getByBusiness(businessId, true),
  });

  const { mutate: createPkg, isLoading: isLoadingCreate } = useMutation(
    (data: ICreateServicePackage) => servicePackageService.create(data),
    {
      onSuccess: () => finishRequest('Pacote criado', 'success'),
      onError: (error: Error) => finishRequest(error.message, 'error'),
    }
  );

  const { mutate: updatePkg, isLoading: isLoadingUpdate } = useMutation(
    (data: IUpdateServicePackage) => servicePackageService.update(data),
    {
      onSuccess: () => finishRequest('Pacote editado', 'success'),
      onError: (error: Error) => finishRequest(error.message, 'error'),
    }
  );

  const finishRequest = (message: string, feedbackType: AlertTypes) => {
    feedback(message, feedbackType);
    if (feedbackType === 'success') {
      refetch();
      onCloseEdit();
    }
  };

  const formRef = useRef<FormHandles>(null);

  useEffect(() => {
    if (pkg) {
      formRef.current?.setData(pkg);
      setSelectedServiceId(pkg.serviceId);
      setActive(pkg.active);
    }
  }, [pkg]);

  const handleSubmit = useCallback(async (data: Record<string, unknown>) => {
    try {
      formRef.current?.setErrors({});
      await schema.validate(data, { abortEarly: false });

      if (!selectedServiceId) {
        feedback('Selecione um serviço', 'error');
        return;
      }

      const commissionPercent = data.commissionPercent
        ? Number(data.commissionPercent)
        : undefined;

      if (packageId) {
        updatePkg({
          id: packageId,
          name: data.name as string,
          description: data.description as string || undefined,
          amount: data.amount as number,
          totalUses: data.totalUses as number,
          validityDays: (data.validityDays as number) || undefined,
          active,
          serviceId: selectedServiceId,
          commissionPercent,
        });
      } else {
        createPkg({
          businessId,
          name: data.name as string,
          description: data.description as string || undefined,
          amount: data.amount as number,
          totalUses: data.totalUses as number,
          validityDays: (data.validityDays as number) || undefined,
          serviceId: selectedServiceId,
          commissionPercent,
        });
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        formRef.current?.setErrors(getValidationErrors(err));
      }
    }
  }, [selectedServiceId, active, packageId, businessId]);

  const selectedService = services?.find(s => s.id === selectedServiceId);

  return !isLoadingPackage || !packageId ? (
    <Form ref={formRef} onSubmit={handleSubmit}>
      <LayoutRegister header={packageId ? 'Editar Pacote' : 'Novo Pacote'} isSubmit isLoading={isLoadingCreate || isLoadingUpdate}>
        <Box display='flex' flexDirection='column' gap={2}>
          <VTextField name='name' label='Nome' required />
          <VTextField name='description' label='Descrição' />
          <VTextFieldCurrency name='amount' label='Valor (R$)' required />
          <VTextField name='totalUses' label='Quantidade de usos' type='number' isMinZero required />
          <VTextField name='validityDays' label='Validade (quantidade de dias, opcional)' type='number' isMinZero />

          <VTextFieldPercentage
            name='commissionPercent'
            label='Comissão (%)'
            helperText='Percentual de comissão para este pacote. Deixe em branco para usar a comissão padrão do profissional.'
          />

          {packageId && (
            <FormControlLabel
              control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
              label="Ativo"
            />
          )}

          <Autocomplete
            options={services || []}
            getOptionLabel={opt => `${opt.description} — R$ ${opt.amount.toFixed(2)}`}
            value={selectedService}
            onChange={(_, value) => setSelectedServiceId(value?.id ?? null)}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            noOptionsText='Nenhum serviço encontrado'
            renderInput={params => (
              <TextField {...params} label='Serviço' variant='outlined' required />
            )}
          />
        </Box>
      </LayoutRegister>
    </Form>
  ) : (
    <Card sx={{ width: 400, height: 200 }}>
      <LinearProgress />
    </Card>
  );
};
